/**
 * The role's Recruiter search (Stage 2, Oct 20 2026): loaded, built by itself when there is none or
 * the brief changed under an unedited one, rebuilt or edited on request. Shown by `SearchBox`.
 *
 * The recruiter's unsaved edit lives HERE, not in the box: "Start sourcing job" commits it first and
 * runs exactly what is on screen, and a failed save keeps the edit (CodeRabbit, PR #665). A build or
 * load answered after the role changed is ignored.
 */
import { useEffect, useRef, useState } from 'react';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import * as api from '../../services/taOutreachApi';
import type { RoleSearch } from '../../types/taOutreach';

export function useRoleSearch(postingId: string) {
  const [search, setSearch] = useState<RoleSearch | null>(null);
  const [building, setBuilding] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  /** The edit on screen, not saved yet; null = the saved string is on screen. */
  const [draft, setDraft] = useState<string | null>(null);
  /** The role's Recruiter project found in the kick-off sheet (Oct 22 2026), for the link field. */
  const [projectId, setProjectId] = useState<string | null>(null);
  /** Bumped per role and per build: an answer for an older one is dropped. */
  const req = useRef(0);
  /**
   * A save still on its way. The server rewrites the whole brief document, so a PUT landing after a
   * rebuild would put the old string back: a rebuild waits for it (CodeRabbit, PR #665).
   */
  const saving = useRef<Promise<string | null> | null>(null);

  async function build(force: boolean) {
    if (saving.current) await saving.current;
    const id = ++req.current;
    setBuilding(true);
    setProblem(null);
    try {
      const r = await api.buildRoleSearch(postingId, force);
      if (id === req.current) {
        setSearch(r.search);
        setDraft(null);
      }
    } catch (err) {
      if (id === req.current) setProblem(err instanceof Error ? err.message : 'The search could not be built just now.');
    } finally {
      if (id === req.current) setBuilding(false);
    }
  }

  useEffect(() => {
    const id = ++req.current;
    // A new role: nothing of the old one may stay on screen or reach a job.
    /* eslint-disable react-hooks/set-state-in-effect -- resetting for the new role is the effect */
    setSearch(null);
    setDraft(null);
    setProjectId(null);
    setProblem(null);
    setBuilding(false);
    /* eslint-enable react-hooks/set-state-in-effect */
    void (async () => {
      try {
        const r = await api.fetchRoleSearch(postingId);
        if (id !== req.current) return;
        setProjectId(r.recruiterProjectId || null);
        // Derived, not a step: none yet, or the brief changed under an unedited one → build it.
        if (!r.search || r.stale) await build(Boolean(r.search));
        else setSearch(r.search);
      } catch (err) {
        if (id === req.current) setProblem(err instanceof Error ? err.message : 'The search could not be loaded.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per role
  }, [postingId]);

  /**
   * Save the edit on screen, if any. Resolves to the string now saved (what a job must run), or null
   * when the save failed — the edit then stays on screen.
   */
  function commit(): Promise<string | null> {
    // One save at a time: a second commit (blur, then Start) joins the one in flight.
    if (!saving.current) {
      saving.current = save().finally(() => {
        saving.current = null;
      });
    }
    return saving.current;
  }

  async function save(): Promise<string | null> {
    const pending = draft?.trim();
    if (!pending || pending === search?.boolean) {
      setDraft(null);
      return search?.boolean || null;
    }
    try {
      const r = await api.saveRoleSearch(postingId, pending, search?.rev ?? 0);
      setSearch(r.search);
      setDraft(null);
      return r.search?.boolean || null;
    } catch (err) {
      showToast({ status: 'error', position: 'bottomCenter', description: err instanceof Error ? err.message : 'Could not save the search.' });
      // A colleague changed it meanwhile: show theirs and keep the edit on screen, so the next save is
      // made on top of their revision instead of failing again (CodeRabbit, PR #750).
      if ((err as { body?: { code?: unknown } } | null)?.body?.code === 'BRIEF_CHANGED') {
        const r = await api.fetchRoleSearch(postingId).catch(() => null);
        if (r?.search) setSearch(r.search);
      }
      return null;
    }
  }

  /** A rule decision rebuilt the string on the server: show it (any unsaved edit is replaced). */
  const replace = (next: RoleSearch) => {
    setSearch(next);
    setDraft(null);
  };

  return { search, building, problem, draft, setDraft, commit, replace, projectId, rebuild: () => build(true) };
}
